import type { PreloadedStateShapeFromReducersMapObject } from '@reduxjs/toolkit';
import type { PartialDeep } from 'type-fest';
import { combineReducers, configureStore } from '@reduxjs/toolkit';

import { analysisApi } from '@/features/analysisApi';
import { fieldApi } from '@/features/fieldSlice';
import { statisticsApi } from '@/features/statisticsSlice';
import worklogReducer, { worklogApi } from '@/features/worklogSlice';
import worklogModalReducer from '@/features/worklogModalSlice';
import filterReducer from '@/features/filterSlice';
import scopeFilterReducer from '@/features/scopeFilterSlice';
import devModeReducer from '@/features/devModeSlice';
import loginReducer from '@/features/loginSlice';
import analysisMapReducer from '@/features/analysisMapSlice';
import mapLayerReducer from '@/features/mapLayerSlice';
import statisticsOptionsReducer from '@/features/statisticsOptionsSlice';
import { scanApi } from '@/features/scanSlice';
import { datasetApi } from '@/features/datasetSlice';
import { networkGroupApi } from '@/features/networkGroupSlice';
import { mapboxApi } from '@/features/searchMapboxSlice';
import { programApi } from '@/features/programSlice';
import { contractorsApi } from '@/features/contractorSlice';
import { listApi } from '@/features/customListSlice';
import { wildfireApi } from '@/features/wildfireSlice';
import { adminApi } from '@/features/adminSlice';
import { reliabilityConfigApi } from '@/features/reliabilityConfigSlice';
import { projectApi } from '@/features/projectSlice';
import { bosunApi } from '@/features/bosunApi';
const reducers = {
  [analysisApi.reducerPath]: analysisApi.reducer,
  [fieldApi.reducerPath]: fieldApi.reducer,
  [statisticsApi.reducerPath]: statisticsApi.reducer,
  [worklogApi.reducerPath]: worklogApi.reducer,
  [mapboxApi.reducerPath]: mapboxApi.reducer,
  [programApi.reducerPath]: programApi.reducer,
  [contractorsApi.reducerPath]: contractorsApi.reducer,
  [listApi.reducerPath]: listApi.reducer,
  [wildfireApi.reducerPath]: wildfireApi.reducer,
  [scanApi.reducerPath]: scanApi.reducer,
  [datasetApi.reducerPath]: datasetApi.reducer,
  [networkGroupApi.reducerPath]: networkGroupApi.reducer,
  [adminApi.reducerPath]: adminApi.reducer,
  [reliabilityConfigApi.reducerPath]: reliabilityConfigApi.reducer,
  [projectApi.reducerPath]: projectApi.reducer,
  [bosunApi.reducerPath]: bosunApi.reducer,
  login: loginReducer,
  analysisMap: analysisMapReducer,
  mapLayer: mapLayerReducer,
  filterQuery: filterReducer,
  scopeFilter: scopeFilterReducer,
  devMode: devModeReducer,
  worklogMap: worklogReducer,
  worklogModal: worklogModalReducer,
  rankingOptions: statisticsOptionsReducer,
};
const rootReducer = combineReducers(reducers);

type PreloadedState = PreloadedStateShapeFromReducersMapObject<typeof reducers>;
export type AppPreloadedState = PartialDeep<PreloadedState>;

// Factory function to create a store - used by both production and tests
export const createAppStore = (preloadedState?: AppPreloadedState) => {
  return configureStore({
    reducer: rootReducer,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({ serializableCheck: false }).concat([
        analysisApi.middleware,
        fieldApi.middleware,
        statisticsApi.middleware,
        worklogApi.middleware,
        programApi.middleware,
        contractorsApi.middleware,
        mapboxApi.middleware,
        listApi.middleware,
        wildfireApi.middleware,
        scanApi.middleware,
        datasetApi.middleware,
        networkGroupApi.middleware,
        adminApi.middleware,
        reliabilityConfigApi.middleware,
        projectApi.middleware,
        bosunApi.middleware,
      ]),
    // Only tests pass preloadedState; production creates the store with none.
    // Redux installs a preloaded slice as given, so the true type is a shallow
    // Partial, while tests supply slices with fields missing. The cast admits
    // that gap without hiding behavior.
    preloadedState: preloadedState as Partial<PreloadedState>,
  });
};

// Production store instance
export const store = createAppStore();

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState>;
// Inferred type: {posts: PostsState, comments: CommentsState, users: UsersState}
export type AppDispatch = typeof store.dispatch;
